/**
 * Sketch-object (furniture/fixture symbol) annotation properties — registration
 * and getters.
 *
 * A SKETCH_OBJECT is a 2D floor-layout symbol (bath, chair, cabinet, …) whose
 * subtype comes from the client-side sketch-object type registry. Shared
 * properties registered here:
 *   - sketchobject_type_id (STRING)     — registry id of the object type. This
 *     value is SCHEMA: per-type params are namespaced on it and panel
 *     visibility conditions match against it — never rename a released id.
 *   - sketchobject_anchor (FLOAT_ARRAY) — flat [x,y,z, …] world coords:
 *     1 point (POINT anchor), 2 points (LINE), n points (POLYGON)
 *   - sketchobject_rotation (FLOAT)     — rotation about Z in radians
 *     (free-placed objects; wall-attached objects derive orientation from
 *     their wall edge)
 *   - sketchobject_width / sketchobject_depth (FLOAT) — footprint size (m)
 *   - sketchobject_color (COLOR)        — glyph line color; defaults to BLACK
 *     (floor-plan symbols print black), not the user default object color
 *     that `annotation_color` resolves to
 *   - sketchobject_dimension_decimals (INT), _show_unit (BOOLEAN),
 *     _text_along_line (BOOLEAN), _text_size (FLOAT) — the length label of
 *     dimension types only
 * Wall attachment (WALL-constrained types only; parametric like
 * OPENING_SKETCH so the object follows wall edits):
 *   - sketchobject_wall_edge_id (STRING) — WallSketchEdgeData.id it sits on
 *   - sketchobject_wall_offset (FLOAT)   — distance along the edge from its
 *                                           start node to the object's near end
 *   - sketchobject_wall_side (BOOLEAN)   — which face of the wall it sits on
 *                                           (true = positive edge normal)
 *   - sketchobject_wall_inset (FLOAT)    — distance off the wall face (m)
 * Conversion:
 *   - sketchobject_source_structure_id (STRING) — identifier of the STRUCTURE
 *     annotation this object was converted from (floor→layout conversion
 *     idempotency link, mirroring openingsketch_source_opening_id)
 * Reserved:
 *   - sketchobject_room_id (STRING)      — for a future IN_ROOM constraint
 *
 * Per-type unique params are registered by the client registry as
 * `sketchobject_<typeId>_<param>` with a visibility condition on
 * `sketchobject_type_id`.
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';
import { TypedFloatArrayProperty } from '../../reactive/TypedFloatArrayProperty';
import type { Coordinate } from './RoomGeometryPropertyGetters';

/** ConvertType.DISTANCE — drives the unit suffix + meter/feet conversion on
 *  distance float inputs (mirrors the value used across the other getters). */
const CONVERT_TYPE_DISTANCE = 1;

function encodeAnchor(coords: Coordinate[]): number[] {
  const arr = new Array<number>(coords.length * 3);
  for (let i = 0; i < coords.length; i++) {
    arr[i * 3] = coords[i].x;
    arr[i * 3 + 1] = coords[i].y;
    arr[i * 3 + 2] = coords[i].z;
  }
  return arr;
}

function decodeAnchor(raw: unknown): Coordinate[] {
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'number') {
    const coords: Coordinate[] = [];
    for (let i = 0; i + 2 < raw.length; i += 3) {
      coords.push({ x: raw[i], y: raw[i + 1], z: raw[i + 2] });
    }
    return coords;
  }
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'object') {
    return raw as Coordinate[];
  }
  if (typeof raw === 'string') {
    return decodeAnchor(JSON.parse(raw));
  }
  return [];
}

export function registerSketchObjectProperties() {
  if (!propertyRegistry.isRegistered('sketchobject_type_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_type_id',
      name: 'object type',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Registry id of the sketch-object type (bath, chair, …). Schema value — never renamed.',
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_anchor')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_anchor',
      name: 'anchor',
      valueType: PropertyValueType.FLOAT_ARRAY,
      defaultValue: [],
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Anchor geometry as flat [x,y,z,…] world coords: 1 point (POINT), 2 (LINE), n (POLYGON)',
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_rotation')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_rotation',
      name: 'rotation',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Rotation about the Z axis in radians (free-placed objects)',
      // Hidden from the panel — driven on-canvas (R key / scroll while placing).
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_width')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_width',
      name: 'width',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 1,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Footprint width along the object local X axis (m)',
      validation: { min: 0.01 },
      // Shown BELOW depth (order 11 vs 10) — for runs (stairs, cabinets) the
      // local-X "width" is the run length, which reads more like a depth, so
      // the across-dimension row leads.
      ui: { group: 'information', order: 11, decimals: 2, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE, hiddenInGroup: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_depth')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_depth',
      name: 'depth',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 1,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Footprint depth along the object local Y axis (m)',
      validation: { min: 0.01 },
      ui: { group: 'information', order: 10, decimals: 2, useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE, hiddenInGroup: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_color')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_color',
      name: 'Object color',
      valueType: PropertyValueType.COLOR,
      // Deliberately a fixed black — NOT the user default object color the
      // generic `annotation_color` falls back to: layout symbols read as
      // floor-plan line work.
      defaultValue: '#000000',
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Line color of the object glyph',
      ui: { group: 'appearance', order: 4 },
    });
  }

  // --- wall attachment (WALL-constrained types) ---
  if (!propertyRegistry.isRegistered('sketchobject_wall_edge_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_wall_edge_id',
      name: 'wall edge',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Identifier (WallSketchEdgeData.id) of the wall edge this object is attached to',
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_wall_offset')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_wall_offset',
      name: 'wall offset',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Distance along the wall edge from its start node to the object near end (m)',
      // Hidden from the panel — driven by dragging along the wall.
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_wall_side')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_wall_side',
      name: 'wall side',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Which face of the wall the object sits against (true = positive edge normal)',
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_wall_inset')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_wall_inset',
      name: 'distance to wall',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Distance between the wall face and the back of the object (m)',
      validation: { min: 0 },
      ui: {
        group: 'information',
        order: 12,
        decimals: 2,
        useUserPreferenceConvertType: CONVERT_TYPE_DISTANCE,
        hiddenInGroup: true,
        // Only meaningful while the object is attached to a wall — free
        // objects have no wall to measure against, so hide it for them.
        visibilityConditions: [{ propertyIdentifier: 'sketchobject_wall_edge_id', value: '', negate: true }],
      },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_source_structure_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_source_structure_id',
      name: 'source structure',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Identifier of the STRUCTURE annotation this object was converted from (conversion idempotency link)',
      ui: { hidden: true },
    });
  }

  // Dimension annotations only (the client's dimension types): the panel
  // shows it for dimensions and leaves it out for every other object.
  if (!propertyRegistry.isRegistered('sketchobject_dimension_decimals')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_dimension_decimals',
      name: 'decimals',
      valueType: PropertyValueType.INT,
      defaultValue: 2,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Decimals of the measured length a dimension shows',
      validation: { min: 0, max: 4 },
      ui: { group: 'information', order: 12, hiddenInGroup: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_dimension_show_unit')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_dimension_show_unit',
      name: 'show unit',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Whether a dimension writes the unit after its length',
      ui: { group: 'information', order: 12.1, hiddenInGroup: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_dimension_text_along_line')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_dimension_text_along_line',
      name: 'text along line',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Whether a dimension turns its length with its line (else it stays upright)',
      ui: { group: 'information', order: 12.2, hiddenInGroup: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_dimension_text_size')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_dimension_text_size',
      name: 'text size',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 1,
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Scale of the length text of a dimension (1 = normal)',
      validation: { min: 0.25, max: 4 },
      // Right after decimals, before the switches.
      ui: { group: 'information', order: 12.05, decimals: 2, hiddenInGroup: true },
    });
  }

  if (!propertyRegistry.isRegistered('sketchobject_room_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'sketchobject_room_id',
      name: 'room',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.SKETCH_OBJECT],
      description: 'Identifier of the room this object belongs to (reserved for an IN_ROOM constraint)',
      ui: { hidden: true },
    });
  }
}

registerSketchObjectProperties();

function prop<T>(identifier: string, sketchObjectId: string): ReactiveProperty<T> {
  const service = getGlobalPropertyService();
  return service.getProperty(identifier, sketchObjectId, PropertyType.PROJECT_GLOBAL) as unknown as ReactiveProperty<T>;
}

export function getSketchObjectTypeIdProperty(id: string): ReactiveProperty<string> {
  return prop<string>('sketchobject_type_id', id);
}

const anchorCache = new Map<string, TypedFloatArrayProperty<Coordinate[]>>();

export function getSketchObjectAnchorProperty(id: string): TypedFloatArrayProperty<Coordinate[]> {
  const cached = anchorCache.get(id);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('sketchobject_anchor', id, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedFloatArrayProperty<Coordinate[]>(raw, encodeAnchor, decodeAnchor);
  anchorCache.set(id, typed);
  return typed;
}

export function getSketchObjectRotationProperty(id: string): ReactiveProperty<number> {
  return prop<number>('sketchobject_rotation', id);
}

export function getSketchObjectWidthProperty(id: string): ReactiveProperty<number> {
  return prop<number>('sketchobject_width', id);
}

export function getSketchObjectDepthProperty(id: string): ReactiveProperty<number> {
  return prop<number>('sketchobject_depth', id);
}

export function getSketchObjectColorProperty(id: string): ReactiveProperty<string> {
  return prop<string>('sketchobject_color', id);
}

export function getSketchObjectWallEdgeIdProperty(id: string): ReactiveProperty<string> {
  return prop<string>('sketchobject_wall_edge_id', id);
}

export function getSketchObjectWallOffsetProperty(id: string): ReactiveProperty<number> {
  return prop<number>('sketchobject_wall_offset', id);
}

export function getSketchObjectWallSideProperty(id: string): ReactiveProperty<boolean> {
  return prop<boolean>('sketchobject_wall_side', id);
}

export function getSketchObjectWallInsetProperty(id: string): ReactiveProperty<number> {
  return prop<number>('sketchobject_wall_inset', id);
}

export function getSketchObjectDimensionDecimalsProperty(id: string): ReactiveProperty<number> {
  return prop<number>('sketchobject_dimension_decimals', id);
}

export function getSketchObjectDimensionShowUnitProperty(id: string): ReactiveProperty<boolean> {
  return prop<boolean>('sketchobject_dimension_show_unit', id);
}

export function getSketchObjectDimensionTextAlongLineProperty(id: string): ReactiveProperty<boolean> {
  return prop<boolean>('sketchobject_dimension_text_along_line', id);
}

export function getSketchObjectDimensionTextSizeProperty(id: string): ReactiveProperty<number> {
  return prop<number>('sketchobject_dimension_text_size', id);
}

/** The panel rows only dimensions show (the object panel leaves them out for everything else). */
export const SKETCH_OBJECT_DIMENSION_PROPERTY_IDS: readonly string[] = [
  'sketchobject_dimension_decimals',
  'sketchobject_dimension_show_unit',
  'sketchobject_dimension_text_along_line',
  'sketchobject_dimension_text_size',
];

export function getSketchObjectRoomIdProperty(id: string): ReactiveProperty<string> {
  return prop<string>('sketchobject_room_id', id);
}

export function getSketchObjectSourceStructureIdProperty(id: string): ReactiveProperty<string> {
  return prop<string>('sketchobject_source_structure_id', id);
}
